import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { fetchWalletDashboard, searchWalletUser } from "../modules/wallet/services/walletService";
import "../css/WalletDashboard.css";
import Layout from "./Layout";

function formatNumber(n) {
  if (n == null) return "-";
  return Number(n).toLocaleString();
}

function toDateInput(isoStr) {
  return isoStr ? isoStr.slice(0, 10) : "";
}

function WalletDashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchError, setSearchError] = useState('');
  const [searching, setSearching] = useState(false);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  useEffect(() => {
    const shouldFetch = (!from && !to) || (from && to);
    if (!shouldFetch) return;

    let cancelled = false;
    async function load() {
      setLoading(true);
      try {
        const result = await fetchWalletDashboard(from || undefined, to || undefined);
        if (!cancelled) setData(result);
      } catch {
        if (!cancelled) setData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [from, to]);

  async function handleSearch() {
    if (!searchQuery.trim()) return;
    setSearchError('');
    setSearching(true);
    try {
      const result = await searchWalletUser(searchQuery.trim());
      navigate(`/wallet/user/${result.memberNo}`, { state: { prefetched: result } });
    } catch {
      setSearchError('검색 결과를 찾을 수 없습니다.');
    } finally {
      setSearching(false);
    }
  }

  function setRecentDays(days) {
    const now = new Date();
    const past = new Date();
    past.setDate(now.getDate() - days);
    setFrom(past.toISOString());
    setTo(now.toISOString());
  }

  function handleFromChange(e) {
    const val = e.target.value;
    if (!val) {
      setFrom("");
      return;
    }
    const [y, m, d] = val.split("-").map(Number);
    setFrom(new Date(y, m - 1, d).toISOString());
  }

  function handleToChange(e) {
    const val = e.target.value;
    if (!val) {
      setTo("");
      return;
    }
    const [y, m, d] = val.split("-").map(Number);
    setTo(new Date(y, m - 1, d).toISOString());
  }

  const kpi = data?.kpi;
  const pending = data?.pending || [];
  const alerts = data?.alerts || {};

  const alertDefs = [
    {
      key: "withdrawalDeadlineImminent",
      className: "wallet-alert-imminent",
      path: "/wallet/withdrawal",
      label: "출금 기한 임박",
    },
    {
      key: "withdrawalDeadlineOverdue",
      className: "wallet-alert-overdue",
      path: "/wallet/withdrawal",
      label: "출금 기한 초과",
    },
    {
      key: "referralDebt",
      className: "wallet-alert-referral",
      path: "/wallet/user",
      label: "레퍼럴 부채 발생",
    },
    {
      key: "cancelProcessingFailures",
      className: "wallet-alert-cancel",
      path: "/wallet/cancel",
      label: "취소 처리 실패",
    },
  ];

  const activeAlerts = alertDefs.filter(({ key }) => (alerts[key]?.count || 0) > 0);

  const kpiCards = [
    { label: "총 지갑 잔액", value: `${formatNumber(kpi?.totalWalletBalance)} P`, color: "#6366f1" },
    { label: "총 CP 잔액",   value: `${formatNumber(kpi?.totalCp)} P`,             color: "#0ea5e9" },
    { label: "총 EP 잔액",   value: `${formatNumber(kpi?.totalEp)} P`,             color: "#10b981" },
    { label: "기간 충전 완료", value: `${formatNumber(kpi?.periodChargeCompleted)} 원`, color: "#f59e0b" },
    { label: "기간 결제 완료", value: `${formatNumber(kpi?.periodPaymentCompleted)} 원`, color: "#ef4444" },
    {
      label: "기간 출금 요청",
      value: `${formatNumber(kpi?.periodWithdrawalRequested?.amount)} 원`,
      sub: `${formatNumber(kpi?.periodWithdrawalRequested?.count)}건`,
      color: "#8b5cf6",
    },
  ];

  return (
    <Layout>
      <div className="wallet-dashboard">
        <div className="wallet-dashboard-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h4 style={{ fontWeight: 700, color: "#1f2937", margin: 0 }}>지갑 대시보드</h4>
          <div className="wallet-dashboard-search" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px' }}>
            <div className="wallet-dashboard-search-row" style={{ display: 'flex', gap: '6px' }}>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => { setSearchQuery(e.target.value); setSearchError(''); }}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                placeholder="사용자명·회원번호·주문ID·계좌ID 검색"
                className="wallet-dashboard-search-input"
                style={{ width: '260px', height: '34px', padding: '0 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '13px', outline: 'none', boxSizing: 'border-box' }}
              />
              <button
                onClick={handleSearch}
                disabled={searching}
                style={{ height: '34px', padding: '0 16px', fontSize: '13px', fontWeight: 600, border: 'none', borderRadius: '8px', background: searching ? '#e5e7eb' : '#6366f1', color: searching ? '#9ca3af' : 'white', cursor: searching ? 'not-allowed' : 'pointer' }}
              >
                {searching ? '검색 중...' : '검색'}
              </button>
            </div>
            {searchError && <span style={{ fontSize: '12px', color: '#ef4444' }}>{searchError}</span>}
          </div>
        </div>

        <div className="wallet-date-filter">
          <input
            type="date"
            className="wallet-date-input"
            value={toDateInput(from)}
            onChange={handleFromChange}
          />
          <span>~</span>
          <input
            type="date"
            className="wallet-date-input"
            value={toDateInput(to)}
            onChange={handleToChange}
          />
          <button className="wallet-filter-btn" onClick={() => setRecentDays(7)}>최근 7일</button>
          <button className="wallet-filter-btn" onClick={() => setRecentDays(30)}>최근 30일</button>
          <button className="wallet-filter-btn" onClick={() => { setFrom(""); setTo(""); }}>전체</button>
        </div>

        {loading && <div className="text-center text-muted py-4">불러오는 중...</div>}

        {!loading && (
          <>
            <div className="wallet-kpi-grid">
              {kpiCards.map(({ label, value, sub, color }) => (
                <div key={label} className="wallet-kpi-card" style={{ borderLeftColor: color }}>
                  <div className="wallet-kpi-label">{label}</div>
                  <div className="wallet-kpi-value">{value}</div>
                  {sub && <div className="wallet-kpi-sub">{sub}</div>}
                </div>
              ))}
            </div>

            <div className="wallet-section">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="mb-0">출금 대기 목록</h5>
                <button
                  onClick={() => navigate("/wallet/withdrawal")}
                  style={{
                    fontSize: "0.78rem", fontWeight: 500,
                    cursor: "pointer", background: "none",
                    color: "#2563eb", border: "none", padding: 0,
                  }}
                >
                  전체 보기
                </button>
              </div>
              {pending.length === 0 ? (
                <div className="wallet-empty">대기 중인 출금 요청이 없습니다</div>
              ) : (
                <table className="table table-hover">
                  <thead>
                    <tr>
                      <th>요청 ID</th>
                      <th>사용자명(회원번호)</th>
                      <th>신청금액 (원)</th>
                      <th>상태</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pending.slice(0, 10).map((item) => (
                      <tr
                        key={item.requestId}
                        style={{ cursor: "pointer" }}
                        onClick={() => navigate("/wallet/withdrawal", { state: { highlightId: item.requestId } })}
                      >
                        <td>{item.requestId}</td>
                        <td>
                          {item.userName}({item.memberNo})
                        </td>
                        <td>{formatNumber(item.requestedAmount)}</td>
                        <td>{{
                          REQUESTED: '요청접수', IN_REVIEW: '검토중',
                          APPROVED: '승인', REJECTED: '거절',
                          COMPLETED: '완료', CANCELED: '취소',
                        }[item.status] || item.status}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <div className="wallet-section">
              <h5>운영 알림</h5>
              {activeAlerts.length === 0 ? (
                <div className="wallet-empty">운영 알림이 없습니다</div>
              ) : (
                activeAlerts.map(({ key, className, path, label }) => (
                  <div
                    key={key}
                    className={`wallet-alert-item ${className}`}
                    onClick={() => navigate(path)}
                  >
                    <strong>{label}</strong> — {alerts[key].count}건
                  </div>
                ))
              )}
            </div>
          </>
        )}
      </div>
    </Layout>
  );
}

export default WalletDashboard;
